import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, ImagePlus, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { imgUrl } from "@/utils/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authFetch = (url, options = {}) => fetch(url, { credentials: "include", ...options });

const EMPTY = { title: "", excerpt: "", image: "", content: "", published: true };

export default function AdminBlogs() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/posts`);
    if (res.ok) setPosts(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", f);
      const res = await authFetch(`${API}/admin/upload`, { method: "POST", body: fd });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      setEditing((d) => ({ ...d, image: data.url }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Add a topic name first.");
      return;
    }
    setSaving(true);
    try {
      const payload = { title: editing.title, excerpt: editing.excerpt, image: editing.image, content: editing.content, published: editing.published };
      const res = editing.id
        ? await authFetch(`${API}/admin/posts/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
        : await authFetch(`${API}/admin/posts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      toast.success(editing.id ? "Guide updated." : "Guide published.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This can't be undone.`)) return;
    const res = await authFetch(`${API}/admin/posts/${id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Guide deleted.");
      load();
    }
  };

  if (editing) {
    return (
      <div className="gold-ring-card rounded-xl p-6 sm:p-8" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl text-amber-900">{editing.id ? "Edit Guide" : "New Guide"}</h2>
          <button onClick={() => setEditing(null)} data-testid="blog-cancel" className="text-stone-400 hover:text-amber-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Topic name *</span>
            <input className="input-dark mt-1.5" placeholder='e.g. "Metallic vs. Flake: Which Epoxy Floor Is Right for You?"' value={editing.title} onChange={(e) => setEditing((d) => ({ ...d, title: e.target.value }))} data-testid="blog-title-input" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Short preview text (shows on the topics page)</span>
            <textarea className="input-dark mt-1.5 min-h-[70px] resize-y" value={editing.excerpt} onChange={(e) => setEditing((d) => ({ ...d, excerpt: e.target.value }))} data-testid="blog-excerpt-input" />
          </label>
          <div>
            <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Cover photo (optional)</span>
            <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="blog-image-upload" />
            {editing.image ? (
              <div className="mt-1.5 relative rounded-lg overflow-hidden border border-amber-600/25 max-w-sm">
                <img src={imgUrl(editing.image)} alt="Cover preview" className="w-full h-40 object-cover" />
                <button
                  onClick={() => setEditing((d) => ({ ...d, image: "" }))}
                  data-testid="blog-image-remove"
                  className="absolute top-2 right-2 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full hover:bg-black/80"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() => imgRef.current?.click()}
                disabled={uploading}
                data-testid="blog-image-upload-btn"
                className="mt-1.5 w-full border-2 border-dashed border-amber-600/30 hover:border-amber-600/60 rounded-lg p-4 flex items-center justify-center gap-2 text-stone-500 hover:text-amber-800 transition-colors duration-300 disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5 text-amber-700" />}
                <span className="text-xs font-semibold">{uploading ? "Uploading..." : "Upload a cover photo from your device"}</span>
              </button>
            )}
            <input className="input-dark mt-2" placeholder="...or paste an image URL" value={editing.image} onChange={(e) => setEditing((d) => ({ ...d, image: e.target.value }))} data-testid="blog-image-input" />
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">Blog content</span>
            <textarea
              className="input-dark mt-1.5 min-h-[280px] resize-y font-mono text-sm"
              placeholder={"Write the guide here. Blank line = new paragraph.\n## Heading — starts a section\n- a dash line — makes a bullet list"}
              value={editing.content}
              onChange={(e) => setEditing((d) => ({ ...d, content: e.target.value }))}
              data-testid="blog-content-input"
            />
          </label>
          <label className="flex items-center gap-3 text-sm text-stone-700 cursor-pointer">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing((d) => ({ ...d, published: e.target.checked }))} className="w-4 h-4 accent-amber-600" data-testid="blog-published-toggle" />
            Visible on the website
          </label>
          <div className="flex justify-end border-t border-amber-600/15 pt-4">
            <button
              onClick={save}
              disabled={saving}
              data-testid="blog-save-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-3.5 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editing.id ? "Save Changes" : "Publish Guide"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blogs-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone-500">{posts.length} guide{posts.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => setEditing({ id: null, ...EMPTY })}
          data-testid="blog-new-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-md shadow-md transition-colors duration-300 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Guide
        </button>
      </div>

      {posts.length === 0 ? (
        <p className="text-stone-500 text-center py-20" data-testid="blogs-empty">No guides yet. Publish your first topic — it helps customers find you on Google.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="gold-ring-card rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid={`blog-row-${p.slug}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-stone-900 truncate">{p.title}</p>
                <p className="text-sm text-stone-500 mt-0.5">{new Date(p.created_at).toLocaleDateString()} · /guides/{p.slug}</p>
              </div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-600"}`}>
                {p.published ? "Live" : "Hidden"}
              </span>
              <div className="flex items-center gap-2">
                {p.published && (
                  <button onClick={() => window.open(`/guides/${p.slug}`, "_blank")} data-testid={`blog-view-${p.slug}`} aria-label="View guide" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><ExternalLink className="w-4 h-4" /></button>
                )}
                <button onClick={() => setEditing({ id: p.id, title: p.title, excerpt: p.excerpt || "", image: p.image || "", content: p.content || "", published: p.published })} data-testid={`blog-edit-${p.slug}`} aria-label="Edit guide" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id, p.title)} data-testid={`blog-delete-${p.slug}`} aria-label="Delete guide" className="p-2 text-stone-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
